import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Flag,
  openDialog,
  Separator,
} from 'platform/components';
import {Heading, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {DeepPartial} from 'utility-types';
import {z} from 'zod';

import {equals} from 'ramda';

import {useListEmailCredentialsQuery} from '@dms/api/core';
import {useGetAiLeadSettingsQuery, useUpdateAiLeadSettingsMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  automatedCommunicationModeOptions,
  BUSINESS_CASE_ONLINE_OFFER,
  INTEREST_OFFER,
} from '../constants/automatedCommunicationModeOptions';
import {AutomatedCommunicationEmailPreview} from './AutomatedCommunicationEmailPreview';

const PROMPT_MIN_ROWS = 6;

const CUSTOMER_HISTORY_ROWS = [
  {
    colorScheme: 'blue',
    label: i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationHistoryNewCustomer'),
    value: i18n.t(
      'page.settings.labels.aiLeadManagementAutomatedCommunicationHistoryNewCustomerRule'
    ),
  },
  {
    colorScheme: 'green',
    label: i18n.t(
      'page.settings.labels.aiLeadManagementAutomatedCommunicationHistoryReturningCustomer'
    ),
    value: i18n.t(
      'page.settings.labels.aiLeadManagementAutomatedCommunicationHistoryReturningCustomerRule'
    ),
  },
  {
    colorScheme: 'red',
    label: i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationHistoryEnquirer'),
    value: i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationHistoryEnquirerRule'),
  },
] as const;

const automatedCommunicationFormSchema = z.object({
  mode: z.enum([BUSINESS_CASE_ONLINE_OFFER, INTEREST_OFFER]),
  emailCredentialsId: z.string().min(1),
  businessCaseOnlineOfferPrompt: z.string().optional(),
  interestOfferPrompt: z.string().optional(),
});

export type AutomatedCommunicationFormType = z.infer<typeof automatedCommunicationFormSchema>;

interface AutomatedCommunicationFormProps extends RequiredTestIdProps {}

export function AutomatedCommunicationForm(props: AutomatedCommunicationFormProps) {
  const {data: aiLeadSettings} = useGetAiLeadSettingsQuery();
  const {data: emailCredentials} = useListEmailCredentialsQuery({ownerType: 'tenant'});
  const [updateAiLeadSettings] = useUpdateAiLeadSettingsMutation();

  const automatedCommunication = aiLeadSettings?.automatedCommunication;

  const emailCredentialsOptions =
    emailCredentials?.credentials?.map((credential) => ({
      value: credential.id,
      label: `${credential.title} (${credential.emailAddress})`,
    })) ?? [];

  const defaultValues: DeepPartial<AutomatedCommunicationFormType> = {
    mode: automatedCommunication?.mode ?? BUSINESS_CASE_ONLINE_OFFER,
    emailCredentialsId: automatedCommunication?.emailCredentialsId ?? undefined,
    businessCaseOnlineOfferPrompt: automatedCommunication?.businessCaseOnlineOfferPrompt ?? '',
    interestOfferPrompt: automatedCommunication?.interestOfferPrompt ?? '',
  };

  const handleOpenEmailPreview = (
    customerMessageId: 'ai_lead-business_case_online_offer' | 'ai_lead-interest_offer'
  ) =>
    openDialog(
      <AutomatedCommunicationEmailPreview
        customerMessageId={customerMessageId}
        data-testid={suffixTestId(`emailPreview-${customerMessageId}`, props)}
      />,
      {title: i18n.t('general.labels.emailPreview')}
    );

  const handleSubmit: FormSubmitHandler<AutomatedCommunicationFormType> = async (values) => {
    await updateAiLeadSettings({
      enabled: aiLeadSettings?.enabled ?? false,
      allowedEmailAddresses: aiLeadSettings?.allowedEmailAddresses ?? [],
      defaultBranchId: aiLeadSettings?.defaultBranchId ?? null,
      automatedCommunication: {
        enabled: true,
        mode: values.mode,
        emailCredentialsId: values.emailCredentialsId,
        businessCaseOnlineOfferPrompt: values.businessCaseOnlineOfferPrompt || null,
        interestOfferPrompt: values.interestOfferPrompt || null,
      },
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  return (
    <Form<AutomatedCommunicationFormType>
      onSubmit={handleSubmit}
      experimentalZodSchema={automatedCommunicationFormSchema}
      defaultValues={defaultValues}
      data-testid={suffixTestId('automatedCommunicationForm', props)}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <Text size="small" color="secondary" data-testid={suffixTestId('description', props)}>
            {i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationDialogDescription')}
          </Text>

          <Alert
            variant="info"
            title={i18n.t(
              'page.settings.labels.aiLeadManagementAutomatedCommunicationSkipConditions'
            )}
            data-testid={suffixTestId('skipConditions', props)}
          />

          <Separator spacing={0} />

          <VStack spacing={2}>
            <Heading size={5} data-testid={suffixTestId('emailCredentialsIdLabel', props)}>
              {i18n.t(
                'page.settings.labels.aiLeadManagementAutomatedCommunicationFallbackSmtpSettings'
              )}
            </Heading>

            <Text
              size="small"
              color="secondary"
              data-testid={suffixTestId('emailCredentialsIdDescription', props)}
            >
              {i18n.t(
                'page.settings.labels.aiLeadManagementAutomatedCommunicationFallbackSmtpSettingsDescription'
              )}
            </Text>

            <FormField
              control={control}
              type="choice"
              name="emailCredentialsId"
              label={i18n.t(
                'page.settings.labels.aiLeadManagementAutomatedCommunicationSelectConnection'
              )}
              options={emailCredentialsOptions}
              menuInPortal
              isRequired
              data-testid={suffixTestId('emailCredentialsId', props)}
            />

            <Text
              size="small"
              color="tertiary"
              data-testid={suffixTestId('emailCredentialsIdHint', props)}
            >
              {i18n.t(
                'page.settings.labels.aiLeadManagementAutomatedCommunicationFallbackSmtpSettingsHint'
              )}
            </Text>
          </VStack>

          <Separator spacing={0} />

          <VStack spacing={2}>
            <Heading size={5} data-testid={suffixTestId('modeLabel', props)}>
              {i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationMode')}
            </Heading>

            {automatedCommunicationModeOptions.map((option) => (
              <Card
                key={option.value}
                variant="inlineWhite"
                title={option.label}
                control={{
                  type: 'radio',
                  value: equals(formApi.watch('mode'), option.value),
                  onChange: () => formApi.setValue('mode', option.value),
                }}
                data-testid={suffixTestId(`mode-${option.value}`, props)}
              >
                <VStack spacing={3}>
                  <Text size="small" color="secondary">
                    {option.description}
                  </Text>

                  <Show when={equals(formApi.watch('mode'), option.value)}>
                    <FormField
                      control={control}
                      type="textarea"
                      name={option.promptName}
                      label={i18n.t(
                        'page.settings.labels.aiLeadManagementAutomatedCommunicationPrompt'
                      )}
                      isResizable
                      minRows={PROMPT_MIN_ROWS}
                      data-testid={suffixTestId(option.promptName, props)}
                    />

                    <Text
                      size="small"
                      color="secondary"
                      data-testid={suffixTestId(`${option.promptName}Hint`, props)}
                    >
                      {i18n.t(
                        'page.settings.labels.aiLeadManagementAutomatedCommunicationPromptHint'
                      )}
                    </Text>

                    <Link
                      size="small"
                      leftIcon="action/visibility"
                      title={i18n.t('general.labels.emailPreview')}
                      onClick={() => handleOpenEmailPreview(option.customerMessageId)}
                      data-testid={suffixTestId(`emailPreviewLink-${option.value}`, props)}
                    />
                  </Show>
                </VStack>
              </Card>
            ))}

            <Text size="small" color="tertiary" data-testid={suffixTestId('modeHint', props)}>
              {i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationModeHint')}
            </Text>
          </VStack>

          <VStack spacing={2} data-testid={suffixTestId('customerHistory', props)}>
            <Text size="xSmall" color="tertiary">
              {i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationHistory')}
            </Text>

            {CUSTOMER_HISTORY_ROWS.map((row) => (
              <HStack key={row.label} spacing={2} align="center">
                <Flag label={row.label} colorScheme={row.colorScheme} size="small" isSubtle />

                <Text size="xSmall" color="tertiary">
                  {row.value}
                </Text>
              </HStack>
            ))}
          </VStack>

          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('cancel', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.save')}
              data-testid={suffixTestId('saveButton', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
